/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MicrophoneIcon, WaveformIcon, SparkleIcon, PlayIcon, LightningIcon } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const Landing = () => {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 grid-bg opacity-40" aria-hidden="true" />
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/5380588/pexels-photo-5380588.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(2px)",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <Logo size={40} withBackground className="glow-primary" />
          <div className="flex flex-col leading-none">
            <span className="font-display font-black text-xl tracking-tight">LUCRÜ</span>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
          <a href="#features" className="hover:text-[#00E5FF] transition-colors">Features</a>
          <a href="#platforms" className="hover:text-[#00E5FF] transition-colors">Platforms</a>
          <a href="#pricing" className="hover:text-[#00E5FF] transition-colors">Pricing</a>
        </div>
        <Link to="/dashboard" data-testid="nav-launch-button">
          <button className="btn-pill btn-primary">
            <PlayIcon weight="fill" size={16} /> Launch App
          </button>
        </Link>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-8 pt-16 pb-24">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <motion.div
            className="md:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF007F]/40 bg-[#FF007F]/10 text-xs text-[#FF007F] mb-3" data-testid="landing-vip-badge">
              <SparkleIcon weight="fill" size={12} />
              <Link to="/vip" className="hover:underline">Top 300 streamers · 1 week Pro free · claim your slot</Link>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#39FF14]/40 bg-[#39FF14]/5 text-xs text-[#39FF14] mb-6" data-testid="landing-trial-badge">
              <SparkleIcon weight="fill" size={12} /> Free 30-day trial · instantly live on all platforms
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight">
              Your chat,
              <br />
              <span className="text-gradient">read out loud.</span>
              <br />
              Live.
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              LUCRÜ is the AI voice co-host that reads your Twitch chat to you in real time,
              reacts with witty comments, and keeps you focused on the game instead of staring at the chat window.
            </p>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <Link to="/dashboard" data-testid="hero-cta-button">
                <button className="btn-pill btn-primary">
                  <PlayIcon weight="fill" size={16} /> Start reading my chat
                </button>
              </Link>
              <a href="#features">
                <button className="btn-pill btn-ghost" data-testid="hero-learn-button">
                  See how it works
                </button>
              </a>
            </div>
            <div className="mt-10 flex items-center gap-8 text-xs text-white/50">
              <div><span className="text-white font-semibold text-xl font-display">11+</span> ultra-realistic voices</div>
              <div><span className="text-white font-semibold text-xl font-display">9</span> live platforms</div>
              <div><span className="text-white font-semibold text-xl font-display">iOS+Android</span> PWA ready</div>
            </div>
          </motion.div>

          {/* Right visual */}
          <motion.div
            className="md:col-span-5"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="glass rounded-2xl p-6 relative overflow-hidden">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#FF007F] flex items-center justify-center glow-primary">
                  <MicrophoneIcon weight="fill" size={18} color="#05050A" />
                </div>
                <div>
                  <div className="text-sm font-semibold">AI Co-host · Nova</div>
                  <div className="text-xs text-[#39FF14] flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" /> Speaking
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  {[12, 24, 16, 32, 20, 28, 14].map((h, i) => (
                    <motion.div
                      key={i}
                      className="viz-bar"
                      animate={{ height: [h, h + 20, h] }}
                      transition={{ duration: 0.6 + i * 0.07, repeat: Infinity, ease: "easeInOut" }}
                      style={{ height: h }}
                    />
                  ))}
                </div>
              </div>

              {[
                { u: "pixel_queen", c: "#FF007F", t: "yo @streamer try the sniper this round" },
                { u: "kekw_42", c: "#39FF14", t: "that clutch tho 😤" },
                { u: "noob_destroyer", c: "#00E5FF", t: "nova read this one please !!" },
              ].map((m, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-3 py-2.5 border-b border-[#222233] last:border-0"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.15 }}
                >
                  <span className="font-semibold text-sm" style={{ color: m.c }}>{m.u}</span>
                  <span className="text-sm text-white/80 flex-1">{m.t}</span>
                  {i === 2 && <WaveformIcon size={16} color="#00E5FF" />}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-8 py-24">
        <div className="mb-16">
          <div className="text-xs text-[#00E5FF] uppercase tracking-widest mb-3">What you get</div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight max-w-2xl">
            Built for streamers who <span className="text-gradient">actually play</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-12 gap-4">
          {[
            {
              col: "md:col-span-7",
              h: 300,
              title: "ElevenLabs voices",
              desc: "Pick from dozens of ultra-realistic voices — French, English, custom cloned. Your co-host sounds human, not robotic.",
              icon: <MicrophoneIcon weight="duotone" size={32} color="#00E5FF" />,
              testid: "feature-tts",
            },
            {
              col: "md:col-span-5",
              h: 300,
              title: "Smart reactions",
              desc: "Toggle smart mode and let Claude Sonnet add a witty 1-liner between reads.",
              icon: <SparkleIcon weight="duotone" size={32} color="#FF007F" />,
              testid: "feature-ai",
            },
            {
              col: "md:col-span-5",
              h: 260,
              title: "Anti-spam & mod",
              desc: "Ban words, ignore users, set min/max length. Only the good stuff reaches your ears.",
              icon: <LightningIcon weight="duotone" size={32} color="#39FF14" />,
              testid: "feature-mod",
            },
            {
              col: "md:col-span-7",
              h: 260,
              title: "Live stats dashboard",
              desc: "See top chatters, messages read, and session uptime — all in a neon dashboard built for 1440p.",
              icon: <WaveformIcon weight="duotone" size={32} color="#00E5FF" />,
              testid: "feature-stats",
            },
          ].map((f, i) => (
            <motion.div
              key={i}
              className={`${f.col} glass rounded-2xl p-8 relative overflow-hidden`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              style={{ minHeight: f.h }}
              data-testid={f.testid}
            >
              <div className="mb-4">{f.icon}</div>
              <h3 className="font-display font-black text-2xl mb-3 tracking-tight">{f.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed max-w-md">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Platforms */}
      <section id="platforms" className="relative z-10 max-w-7xl mx-auto px-8 py-16">
        <div className="text-xs text-[#00E5FF] uppercase tracking-widest mb-3">Platforms</div>
        <h2 className="font-display text-4xl font-black tracking-tight mb-10">Where streamers live.</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: "Twitch", status: "Live now · auto chat" },
            { name: "YouTube Live", status: "Live now · auto chat" },
            { name: "Kick", status: "Live now · auto chat" },
            { name: "Chaturbate", status: "Live · via mobile relay" },
            { name: "OnlyFans", status: "Live · via mobile relay" },
            { name: "Pornhub", status: "Live · via mobile relay" },
            { name: "Camsoda", status: "Live · via mobile relay" },
            { name: "Stripchat", status: "Live · via mobile relay" },
            { name: "ManyVids", status: "Live · via mobile relay" },
          ].map((p, i) => (
            <div
              key={p.name}
              className="glass rounded-2xl p-5 flex flex-col gap-2"
              data-testid={`platform-card-${p.name.toLowerCase().split(" ")[0]}`}
            >
              <div className="font-display font-black text-lg">{p.name}</div>
              <div className="text-xs text-white/50">{p.status}</div>
              <div className={`mt-2 w-2 h-2 rounded-full ${p.status.startsWith("Live") ? "bg-[#39FF14]" : "bg-white/20"}`} />
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#222233] py-8 mt-16">
        <div className="max-w-7xl mx-auto px-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <div className="flex items-center gap-3">
            <Logo size={28} tone="light" />
            <div>
              <span className="font-display font-black text-white/60 tracking-widest">LUCRÜ</span>
              <span className="text-white/20 mx-2">·</span>
              <span>LUCRÜ™ is a licensed product of LUCRÜ</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-[#00E5FF]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#00E5FF]">Privacy</Link>
            <Link to="/leaderboard" className="hover:text-[#00E5FF]">Leaderboard</Link>
          </div>
          <div>© 2026 LUCRÜ · All rights reserved</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
